import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebaseConfig";
import { navigateTo } from "../../utils/navigation";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";


interface SessionTimeoutWarningProps {
  minutesLeft: number;
  onExtend: () => void;
}

export default function SessionTimeoutWarning({
  minutesLeft,
  onExtend,
}: SessionTimeoutWarningProps) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(minutesLeft * 60);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
      }
    } catch {
    }
    await signOut(auth);
    navigateTo("/signin");
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-800">
        <div className="text-center space-y-4">
          <div className="text-5xl">⏰</div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {t("session.expiringTitle", "Tu sesión está por expirar")}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t(
              "session.expiringMessage",
              "Por seguridad, tu sesión se cerrará automáticamente por inactividad."
            )}
          </p>
          <div className="text-4xl font-bold text-brand-500">
            {minutes}:{seconds.toString().padStart(2, "0")}
          </div>
          <div className="flex gap-3 pt-4">
            <button
              onClick={onExtend}
              className="flex-1 px-4 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition"
            >
              {t("groups.continuarsesion")}
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition"
            >
              {t("groups.cerrarsesion")}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t(
              "session.autoLogoutNotice",
              "Si no haces nada, tu sesión se cerrará automáticamente"
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
